import { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Container, Row } from "react-grid-system";
import { useBlocker } from "react-router-dom";
import {
  getDisplayError,
  getDisplayMessages,
  HTTPError,
  NetworkDisplayError,
} from "../../lib/api/endpoints";
import * as models from "../../lib/api/models";
import {
  useAddNote,
  useCallEnded,
  useGetAccount,
  useGetAccountDetails,
  useGetCallInfo,
  useGetListen,
  useSubmitResultResearchOnly,
} from "../../lib/api/queries";
import { reportError } from "../../lib/bugReporting";
import {
  ToolState,
  ToolType,
  useToolState,
} from "../../modules/AccountDetails";
import CloseToolConfirmationModal from "../../modules/AccountDetails/CloseToolConfirmationModal/CloseToolConfirmationModal";
import CompletedTool from "../../modules/AccountDetails/CompletedTool";
import { toolTypeToName } from "../../modules/AccountDetails/utils";
import AccountDetailsHeader from "../../modules/AccountDetailsHeader";
import CMInfo from "../../modules/CMInfo";
import ChangeUsername from "../../modules/ChangeUsername";
import CreditLineIncrease from "../../modules/CreditLineIncrease";
import CycleToDateTransactions from "../../modules/CycleToDateTransactions";
import LostStolen from "../../modules/LostStolen";
import MakePayment from "../../modules/MakePayment";
import Autopay from "../../modules/ManageAutopay";
import PaymentHistory from "../../modules/PaymentHistory";
import PaymentTransactions from "../../modules/PaymentTransactions";
import PermissionToDiscuss from "../../modules/PermissionToDiscuss";
import Phones from "../../modules/Phones";
import SendTestPushNotification from "../../modules/SendTestPushNotification";
import ManageSourceAccounts from "../../modules/SourceAccounts";
import StatementHistory from "../../modules/StatementHistory";
import StyledError from "../../modules/common/StyledError";
import Terms from "../../modules/Terms";
import Toolbar from "../../modules/Toolbar";
import Spinner from "../../modules/common/Spinner";
import { NetworkModal } from "../../modules/common/modals";
import styles from "./AccountDetailsPage.module.css";
import OnlineStatements from "../../modules/OnlineStatements";
import PaperlessService from "../../modules/PaperlessService";
import CardActivation from "../../modules/CardActivation";
import TextBankingServices from "../../modules/TextBankingServices";
import DataPanel from "../../modules/DataPanel";
import Results from "../../modules/Results";
import TransferMessages from "../../modules/TransferMessages";
import DebtDisclosure from "../../modules/DebtDisclosure";
import { useChannelState } from "../../lib/channelContext";
import { ChannelMapToFull } from "../../lib/channelUtil";
import WarningModal from "../../modules/common/modals/WarningModal/WarningModal";
import DebtDisclosureRequiredModal from "../../modules/DebtDisclosureRequiredModal";
import NotesFooter from "../../modules/NotesFooter";
import Notes from "../../modules/Notes";
import classNames from "classnames";
import { useIsAgentCMInfo } from "../../lib/roleContext";
import { useListener } from "../../lib/useListener";
import { useDialerPullState } from "../../lib/dialerPullContext";
import WarningBanner from "../../modules/WarningBanner";
import { useLocationCallContext } from "../../lib/dialerUtil";
import { paths } from "../../lib/router";
import StyledLink from "../../modules/common/StyledLink";

const strings = {
  errorTitle: "Account failed to load",

  resultModalLabel: (c: models.Channel) =>
    `Result Required for ${ChannelMapToFull[c]} Channel Modal`,
  resultCanComplete: (c: models.Channel) =>
    `Would you like to complete a Result prior to leaving this page or starting a New Search? Result is not required when on the ${ChannelMapToFull[c]} Channel.`,
  resultMustComplete: (c: models.Channel) =>
    `Result tool must be completed prior to leaving this page or starting a New Search when on the ${ChannelMapToFull[c]} Channel.`,
  resultButtonText: "Go to Results",
  endSessionText: "End Session",
  a0054: `A_0054 - Note contains invalid characters. Please review your note. Please delete any character that are not displayed on the keyboard and try again. Allowable keyboard characters include: a-z,A-Z,0-9 and !@#$%^&*()[]{}?/|-=+.;,:<>${"`"}'~"_ and spacebar.`,
  invalidNote:
    "Unable to submit currently in-progress note. Please resolve issues and retry.",
  superseded:
    "The preview you were viewing has been superseded by a call. Please result this preview immediately so you can load the new call information.",
  getCallInfoErrorModal: "Get Call Info Error Modal",
  getCallInfoError:
    "There was a problem starting your session. Please try again. If this problem persists, please close this window and retry in a new session.",
  retry: "Retry",
  getListenFailure:
    "We are experiencing difficulty automatically pulling call information from the dialer at this time. Please continue and complete this call as appropriate.",

  returnToAccountSearchP1:
    "If you are still unable to load this account's data, please return to the",
  accountSearchPage: "Account Search Page",
  returnToAccountSearchP2: "and search for this account again.",
};

const COMPLETED_TOOLS_ID = "completed-tools";
const NOTE_REGEX = /^[A-z0-9 !@#$%^&*()[\]{}?/|\-=+.;,:<>`'~"_\n]+$/;
const LISTENER_TIMEOUT_MS = 5000;
const MAX_NUM_GET_LISTEN_FAILURES = 5;

function isDebtDisclosureBlocking({
  debtDisclosure,
  isDebtDisclosureComplete,
  activeTools,
}: {
  debtDisclosure: models.DebtDisclosure | null | undefined;
  isDebtDisclosureComplete: boolean;
  activeTools: ToolState[];
}) {
  if (
    import.meta.env.DEV &&
    import.meta.env.VITE_DEV_ENABLE_BLOCKING_OVERRIDE === "true"
  ) {
    return false;
  }
  return (
    !!debtDisclosure &&
    (!isDebtDisclosureComplete ||
      !!activeTools.find(
        (tool) => tool.type === ToolType.DebtDisclosure && tool.inProgress,
      ))
  );
}

function isResultBlocking({ isResultComplete }: { isResultComplete: boolean }) {
  if (
    import.meta.env.DEV &&
    import.meta.env.VITE_DEV_ENABLE_BLOCKING_OVERRIDE === "true"
  ) {
    return false;
  }
  return !isResultComplete;
}

function isInProgressBlocking({ activeTools }: { activeTools: ToolState[] }) {
  if (
    import.meta.env.DEV &&
    import.meta.env.VITE_DEV_ENABLE_BLOCKING_OVERRIDE === "true"
  ) {
    return false;
  }
  return activeTools.some((tool) => tool.inProgress);
}

function isBlocking({
  debtDisclosure,
  isDebtDisclosureComplete,
  isResultComplete,
  activeTools,
}: {
  debtDisclosure: models.DebtDisclosure | null | undefined;
  isDebtDisclosureComplete: boolean;
  isResultRequired: boolean;
  isResultComplete: boolean;
  activeTools: ToolState[];
}) {
  if (
    import.meta.env.DEV &&
    import.meta.env.VITE_DEV_ENABLE_BLOCKING_OVERRIDE === "true"
  ) {
    return false;
  }
  return (
    isInProgressBlocking({ activeTools }) ||
    isResultBlocking({ isResultComplete }) ||
    isDebtDisclosureBlocking({
      debtDisclosure,
      isDebtDisclosureComplete,
      activeTools,
    })
  );
}

type Props = {
  dwbuid: string;
};

const AccountDetailsPage = ({ dwbuid }: Props) => {
  const { channel } = useChannelState();

  const canViewCMInfo = useIsAgentCMInfo();

  const [readDebtDisclosure, setReadDebtDisclosure] = useState<
    boolean | undefined
  >();
  const [isDebtDisclosureComplete, setIsDebtDisclosureComplete] =
    useState(false);

  const isResultRequired = useMemo(
    () => channel !== "RES" && channel !== "COR",
    [channel],
  );
  const [isResultComplete, setIsResultComplete] = useState<boolean>(false);

  const accountDetailsQueryResult = useGetAccountDetails(dwbuid);

  const locationCallContext = useLocationCallContext();
  const { initialListeningTime, callContext, setCallContext } =
    useDialerPullState();
  const [nextCallContext, setNextCallContext] =
    useState<models.CallContext | null>(null);

  const { mutateAsync: getListen } = useGetListen();
  const [isSuperseded, setIsSuperseded] = useState(false);
  const [numGetListenFailures, setNumGetListenFailures] = useState(0);
  const [hideGetListenFailureBanner, setHideGetListenFailureBanner] =
    useState(false);

  const {
    data: getAccountData,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useGetAccount(dwbuid);

  const { mutateAsync: callEnded } = useCallEnded();

  const onCallEnded = useCallback(async () => {
    if (!callContext || callContext.callId !== "0") {
      return;
    }
    try {
      await callEnded({ callContextId: callContext.id });
    } catch (err) {
      reportError(
        new Error("Failed to end preview on call connection", { cause: err }),
      );
    }
  }, [callEnded, callContext]);

  const onListenerCallback = useCallback(
    async (resetListener: () => void) => {
      if (!callContext || callContext.callId !== "0") {
        return;
      }

      try {
        const data = await getListen(initialListeningTime);
        if (data.customerCallContext) {
          if (data.customerCallContext.dwbuid === dwbuid) {
            await onCallEnded();
            setCallContext(data.customerCallContext);
          } else {
            setNextCallContext(data.customerCallContext);
            setIsSuperseded(true);
          }
        } else {
          resetListener();
        }
      } catch (err) {
        setNumGetListenFailures((prev) => prev + 1);
        reportError(
          new Error("Failed to get listen on account details", { cause: err }),
        );
        resetListener();
      }
    },
    [
      initialListeningTime,
      callContext,
      onCallEnded,
      getListen,
      dwbuid,
      setCallContext,
    ],
  );

  useListener({ onListenerCallback, listenerTimeoutMs: LISTENER_TIMEOUT_MS });

  const autoOpenTools = useMemo(() => {
    const tools: ToolType[] = [];

    if (!accountDetailsQueryResult.data) {
      return;
    }

    if (accountDetailsQueryResult.data.debtDisclosure) {
      tools.push(ToolType.DebtDisclosure);
    } else {
      switch (channel) {
        case "INB":
        case "TRN":
        case "COR":
          if (canViewCMInfo) {
            tools.push(ToolType.CMInfo);
          }
          break;
        case "OUT":
        case "RES":
          tools.push(ToolType.Notes);
          break;
      }
    }

    return tools;
  }, [channel, canViewCMInfo, accountDetailsQueryResult.data]);
  const toolState = useToolState(autoOpenTools);

  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (
        !isBlocking({
          debtDisclosure: accountDetailsQueryResult.data?.debtDisclosure,
          isDebtDisclosureComplete,
          isResultComplete,
          isResultRequired,
          activeTools: toolState.activeTools,
        }) ||
        isError
      ) {
        return;
      }
      // show browser specific message (not customizable)
      e.preventDefault();
    };

    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [
    isError,
    accountDetailsQueryResult.data,
    isDebtDisclosureComplete,
    isResultRequired,
    isResultComplete,
    toolState.activeTools,
  ]);

  const blocker = useBlocker(
    !isError &&
      isBlocking({
        debtDisclosure: accountDetailsQueryResult.data?.debtDisclosure,
        isDebtDisclosureComplete,
        isResultComplete,
        isResultRequired,
        activeTools: toolState.activeTools,
      }),
  );

  const [isBlockerModalOpen, setIsBlockerModalOpen] = useState(false);

  useEffect(() => {
    if (blocker.state === "blocked") {
      setIsBlockerModalOpen(true);
    }
  }, [blocker]);

  const handleShouldBlockProceed = (proceed: boolean) => {
    if (blocker.state === "blocked") {
      if (proceed) {
        blocker.proceed();
      } else {
        blocker.reset();
      }
    }
    setIsBlockerModalOpen(false);
  };

  const inProgressToolNames = useMemo(
    () =>
      toolState.activeTools
        .filter((tool) => tool.inProgress)
        .map((tool) => toolTypeToName(tool.type)),
    [toolState.activeTools],
  );

  const [closeConfirmationTool, setCloseConfirmationTool] =
    useState<ToolState>();
  const onClickCloseTool = (
    tool: ToolState,
    bypassProgressConfirmation?: boolean,
  ) => {
    if (
      tool.inProgress &&
      !closeConfirmationTool &&
      !bypassProgressConfirmation
    ) {
      setCloseConfirmationTool(tool);
    } else {
      toolState.onRequestCloseTool(tool.id);
      setCloseConfirmationTool(undefined);
    }
  };

  const [displayError, setDisplayError] = useState<NetworkDisplayError>();
  const [
    hasSubmittedCreditLineIncreaseThisSession,
    setHasSubmittedCreditLineIncreaseThisSession,
  ] = useState(false);

  const {
    data: getCallInfoData,
    isLoading: isGetCallInfoLoading,
    isError: isGetCallInfoError,
    error: getCallInfoError,
    refetch: getCallInfoRefetch,
  } = useGetCallInfo({
    partyId: getAccountData?.partyId ?? "",
    dwbuid,
    callId: locationCallContext?.callId ?? "",
    channel: channel,
    // TODO: phoneNumber after call context
  });

  const {
    mutateAsync: submitResultResearchOnly,
    isLoading: isResultResearchOnlyLoading,
  } = useSubmitResultResearchOnly();

  // Silently report GET callId errors to sentry
  useEffect(() => {
    if (isGetCallInfoError) {
      reportError(new Error("Programmer Error: Error retrieving callId info"), {
        error: getCallInfoError,
      });
    }
  }, [getCallInfoError, isGetCallInfoError]);

  const onDisplayGeneralErrors = useCallback(
    (err: unknown) => setDisplayError(getDisplayError(err)),
    [],
  );

  const onDisplayMessages = useCallback((data: models.MessagesResponse) => {
    if (data.messages.length > 0) {
      setDisplayError(getDisplayMessages(data));
    }
  }, []);

  // NOTE FOOTER
  const { mutateAsync: addNote, isLoading: isAddNoteLoading } = useAddNote();
  const [note, setNote] = useState("");
  const [isSubmittingNoteViaFooter, setIsSubmittingNoteViaFooter] =
    useState(false);
  const [isNoteCritical, setIsNoteCritical] = useState(false);
  const [noteValidationMessages, setNoteValidationMessages] = useState<
    string[]
  >([]);
  async function onSubmitNote() {
    const trimmedNote = note.trim();
    if (isAddNoteLoading || !trimmedNote) {
      return;
    }

    if (!NOTE_REGEX.test(trimmedNote)) {
      setNoteValidationMessages([strings.a0054]);
      return;
    }

    setNoteValidationMessages([]);
    setIsSubmittingNoteViaFooter(true);

    try {
      const data = await addNote({
        dwbuid,
        content: trimmedNote.toUpperCase(),
        channel: ChannelMapToFull[channel],
        priority: isNoteCritical ? "CRITICAL" : "REGULAR",
        referenceId: getCallInfoData?.referenceId,
      });
      setNote("");
      setIsNoteCritical(false);
      onDisplayMessages(data);
    } catch (err) {
      onDisplayGeneralErrors(err);
    }
    setIsSubmittingNoteViaFooter(false);
  }

  async function onResultSubmitNote() {
    const trimmedNote = note.trim();
    if (isAddNoteLoading || !trimmedNote) {
      return;
    }

    if (!NOTE_REGEX.test(trimmedNote)) {
      setNoteValidationMessages([strings.a0054]);
      throw new HTTPError({
        message: strings.invalidNote,
        status: 400,
        validation: {
          errors: {
            general: [strings.invalidNote],
          },
        },
      });
    }

    setNoteValidationMessages([]);

    const data = await addNote({
      dwbuid,
      content: trimmedNote.toUpperCase(),
      channel: ChannelMapToFull[channel],
      priority: isNoteCritical ? "CRITICAL" : "REGULAR",
      referenceId: getCallInfoData?.referenceId,
    });

    setNote("");
    setIsNoteCritical(false);
    onDisplayMessages(data);
  }

  useEffect(() => {
    const beforeUnloadCallback = (event: BeforeUnloadEvent) => {
      if (isResultComplete) {
        return false;
      }
      event.preventDefault();
      return true;
    };

    window.addEventListener("beforeunload", beforeUnloadCallback);
    return () => {
      window.removeEventListener("beforeunload", beforeUnloadCallback);
    };
  }, [isResultComplete]);

  // Reset all state values when DWBUID changes
  useEffect(() => {
    setReadDebtDisclosure(undefined);
    setIsDebtDisclosureComplete(false);
    setIsResultComplete(false);
    setIsSuperseded(false);
    setNumGetListenFailures(0);
    setHideGetListenFailureBanner(false);
    setIsBlockerModalOpen(false);
    setDisplayError(undefined);
    setHasSubmittedCreditLineIncreaseThisSession(false);
    setNote("");
    setIsSubmittingNoteViaFooter(false);
    setIsNoteCritical(false);
    setNoteValidationMessages([]);
    setNextCallContext(null);
  }, [dwbuid]);

  const renderTool = (
    tool: ToolState,
    accountData: models.GetAccountResponse,
  ): JSX.Element => {
    const type = tool.type;
    switch (type) {
      case ToolType.ChangeUsername:
        return (
          <ChangeUsername
            key={tool.id}
            toolId={tool.id}
            account={accountData}
            accountDetailsQueryResult={accountDetailsQueryResult}
            onSuccess={(summaryWithoutTime) =>
              toolState.onCompleteTool(tool.id, summaryWithoutTime)
            }
            onDisplayGeneralErrors={onDisplayGeneralErrors}
            onRequestClose={() => onClickCloseTool(tool)}
          />
        );
      case ToolType.TestPushNotification:
        return (
          <SendTestPushNotification
            key={tool.id}
            toolId={tool.id}
            account={accountData}
            onDisplayGeneralErrors={onDisplayGeneralErrors}
            onRequestClose={() => onClickCloseTool(tool)}
            referenceId={getCallInfoData?.referenceId}
          />
        );
      case ToolType.PaymentTransactions:
        return (
          <PaymentTransactions
            key={tool.id}
            toolId={tool.id}
            account={accountData}
            openScheduledPayment={(editPaymentData) => {
              toolState.onAddTool({
                type: ToolType.MakePayment,
                inProgress: false,
                editPaymentData,
              });
              requestAnimationFrame(() => {
                window.scrollTo({ top: 0, behavior: "smooth" });
              });
            }}
            activeTools={toolState.activeTools}
            onRequestClose={() => onClickCloseTool(tool)}
            onDisplayGeneralErrors={onDisplayGeneralErrors}
          />
        );
      case ToolType.PaymentHistory:
        return (
          <PaymentHistory
            key={tool.id}
            account={accountData}
            toolId={tool.id}
            onRequestClose={() => onClickCloseTool(tool)}
          />
        );
      case ToolType.MakePayment:
        return (
          <MakePayment
            key={tool.id}
            account={accountData}
            accountDetailsQueryResult={accountDetailsQueryResult}
            onClose={() => {
              toolState.onRequestCloseTool(tool.id);
            }}
            onRequestClose={() => onClickCloseTool(tool)}
            onComplete={(summaryWithoutTime: string) => {
              toolState.onCompleteTool(tool.id, summaryWithoutTime);
            }}
            onSetToolInProgress={toolState.onSetToolInProgress}
            tool={tool}
            onDisplayGeneralErrors={onDisplayGeneralErrors}
          />
        );
      case ToolType.SourceAccounts:
        return (
          <ManageSourceAccounts
            key={tool.id}
            account={accountData}
            accountDetailsQueryResult={accountDetailsQueryResult}
            tool={tool}
            onSetToolInProgress={toolState.onSetToolInProgress}
            onDisplayGeneralErrors={onDisplayGeneralErrors}
            onRequestClose={() => onClickCloseTool(tool)}
          />
        );
      case ToolType.PermissionToDiscuss:
        return (
          <PermissionToDiscuss
            key={tool.id}
            account={accountData}
            toolId={tool.id}
            onSetToolInProgress={toolState.onSetToolInProgress}
            onDisplayGeneralErrors={onDisplayGeneralErrors}
            onRequestClose={() => onClickCloseTool(tool)}
            referenceId={getCallInfoData?.referenceId}
          />
        );
      case ToolType.CycleToDateTransactions:
        return (
          <CycleToDateTransactions
            key={tool.id}
            account={accountData}
            toolId={tool.id}
            onRequestClose={() => onClickCloseTool(tool)}
          />
        );
      case ToolType.StatementHistory:
        return (
          <StatementHistory
            key={tool.id}
            toolId={tool.id}
            onRequestClose={() => onClickCloseTool(tool)}
            account={accountData}
          />
        );
      case ToolType.LostStolen:
        return (
          <LostStolen
            key={tool.id}
            account={accountData}
            toolId={tool.id}
            onRequestClose={(bypassProgressConfirmation) =>
              onClickCloseTool(tool, bypassProgressConfirmation)
            }
            onSuccess={(summaryWithoutTime) =>
              toolState.onCompleteTool(tool.id, summaryWithoutTime)
            }
            onSetToolInProgress={toolState.onSetToolInProgress}
            referenceId={getCallInfoData?.referenceId}
            onDisplayGeneralErrors={onDisplayGeneralErrors}
          />
        );
      case ToolType.CreditLineIncrease:
        return (
          <CreditLineIncrease
            account={accountData}
            key={tool.id}
            toolId={tool.id}
            onRequestClose={() => onClickCloseTool(tool)}
            onSetToolInProgress={toolState.onSetToolInProgress}
            onDisplayGeneralErrors={onDisplayGeneralErrors}
            referenceId={getCallInfoData?.referenceId}
            hasSubmittedCreditLineIncreaseThisSession={
              hasSubmittedCreditLineIncreaseThisSession
            }
            onSubmitCreditLineIncrease={() =>
              setHasSubmittedCreditLineIncreaseThisSession(true)
            }
          />
        );
      case ToolType.Terms:
        return (
          <Terms
            account={accountData}
            key={tool.id}
            toolId={tool.id}
            onRequestClose={() => onClickCloseTool(tool)}
          />
        );
      case ToolType.ManageAutopay:
        return (
          <Autopay
            key={tool.id}
            tool={tool}
            account={accountData}
            accountDetailsQueryResult={accountDetailsQueryResult}
            onRequestClose={() => onClickCloseTool(tool)}
            onDisplayGeneralErrors={onDisplayGeneralErrors}
            onComplete={(summaryWithoutTime: string) => {
              toolState.onCompleteTool(tool.id, summaryWithoutTime);
            }}
            onSetToolInProgress={toolState.onSetToolInProgress}
            referenceId={getCallInfoData?.referenceId}
          />
        );
      case ToolType.Phones:
        return (
          <Phones
            key={tool.id}
            toolId={tool.id}
            account={accountData}
            onRequestClose={() => onClickCloseTool(tool)}
            onSetToolInProgress={toolState.onSetToolInProgress}
            onDisplayGeneralErrors={onDisplayGeneralErrors}
            referenceId={getCallInfoData?.referenceId}
          />
        );
      case ToolType.CMInfo:
        return (
          <CMInfo
            key={tool.id}
            dwbuid={accountData.currentDwbuid}
            onRequestClose={() => onClickCloseTool(tool)}
            toolId={tool.id}
            onSetToolInProgress={toolState.onSetToolInProgress}
            onDisplayGeneralErrors={onDisplayGeneralErrors}
            referenceId={getCallInfoData?.referenceId}
            onOpenPermissionToDiscussTool={() => {
              const activeP2DToolState = toolState.activeTools.find(
                (activeTool) =>
                  activeTool.type === ToolType.PermissionToDiscuss,
              );
              if (activeP2DToolState) {
                document
                  .getElementById(activeP2DToolState.id)
                  ?.scrollIntoView({ behavior: "smooth" });
              } else {
                toolState.onAddTool({
                  type: ToolType.PermissionToDiscuss,
                  inProgress: false,
                });
                requestAnimationFrame(() => {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                });
              }
            }}
          />
        );
      case ToolType.OnlineStatements:
        return (
          <OnlineStatements
            key={tool.id}
            dwbuid={accountData.currentDwbuid}
            onRequestClose={() => onClickCloseTool(tool)}
            toolId={tool.id}
          />
        );
      case ToolType.PaperlessService:
        return (
          <PaperlessService
            key={tool.id}
            dwbuid={accountData.currentDwbuid}
            partyId={accountData.partyId}
            referenceId={getCallInfoData?.referenceId}
            onRequestClose={() => onClickCloseTool(tool)}
            toolId={tool.id}
            onDisplayGeneralErrors={onDisplayGeneralErrors}
            onSetToolInProgress={toolState.onSetToolInProgress}
            isDelinquent={
              accountDetailsQueryResult.data?.internalStatusCode === "D" ||
              accountDetailsQueryResult.data?.internalStatusCode === "X"
            }
            // If account is lost (internal status blank, external status L)
            // Then all fields should be disabled but unchanged from their original values
            isDisabled={
              accountDetailsQueryResult.data?.internalStatusCode === "" &&
              accountDetailsQueryResult.data?.externalStatusCode === "L"
            }
          />
        );
      case ToolType.CardActivation:
        return (
          <CardActivation
            key={tool.id}
            className={styles.tool}
            dwbuid={accountData.currentDwbuid}
            toolId={tool.id}
            onRequestClose={() => onClickCloseTool(tool)}
            onDisplayGeneralErrors={onDisplayGeneralErrors}
            onSetToolInProgress={toolState.onSetToolInProgress}
            onSuccess={(summaryWithoutTime) =>
              toolState.onCompleteTool(tool.id, summaryWithoutTime)
            }
            referenceId={getCallInfoData?.referenceId}
          />
        );
      case ToolType.TextBanking:
        return (
          <TextBankingServices
            key={tool.id}
            toolId={tool.id}
            dwbuid={accountData.currentDwbuid}
            onRequestClose={() => onClickCloseTool(tool)}
            onDisplayGeneralErrors={onDisplayGeneralErrors}
          />
        );
      case ToolType.Results:
        return (
          <Results
            key={tool.id}
            toolId={tool.id}
            dwbuid={accountData.currentDwbuid}
            referenceId={getCallInfoData?.referenceId}
            activeTools={toolState.activeTools}
            nextCallContext={nextCallContext}
            accountDetails={accountDetailsQueryResult.data}
            onSetToolInProgress={toolState.onSetToolInProgress}
            onCloseTool={toolState.onRequestCloseTool}
            onDisplayGeneralErrors={onDisplayGeneralErrors}
            setIsResultComplete={setIsResultComplete}
            isDebtDisclosureBlocking={isDebtDisclosureBlocking({
              debtDisclosure: accountDetailsQueryResult.data?.debtDisclosure,
              isDebtDisclosureComplete,
              activeTools: toolState.activeTools,
            })}
            onOpenDebtDisclosure={() => {
              if (accountDetailsQueryResult.data?.debtDisclosure) {
                toolState.onAddTool({
                  type: ToolType.DebtDisclosure,
                  inProgress: false,
                });
              }
            }}
            onSubmitNote={onResultSubmitNote}
            isNoteSubmitting={isAddNoteLoading && !isSubmittingNoteViaFooter}
          />
        );
      case ToolType.DebtDisclosure:
        return (
          <DebtDisclosure
            key={tool.id}
            toolId={tool.id}
            dwbuid={dwbuid}
            referenceId={getCallInfoData?.referenceId}
            disabled={isDebtDisclosureComplete && !!readDebtDisclosure}
            stateCode={accountDetailsQueryResult.data?.stateCode}
            disclosure={
              accountDetailsQueryResult.data?.debtDisclosure ?? undefined
            }
            onCloseTool={toolState.onRequestCloseTool}
            onSubmit={() => {
              toolState.onSetToolInProgress(tool.id, false);
              setIsDebtDisclosureComplete(true);
              toolState.onRequestCloseTool(tool.id);
            }}
            readDebtDisclosure={readDebtDisclosure}
            setReadDebtDisclosure={setReadDebtDisclosure}
            onSetToolInProgress={toolState.onSetToolInProgress}
            onDisplayGeneralErrors={onDisplayGeneralErrors}
          />
        );
      case ToolType.Notes:
        return (
          <Notes
            key={tool.id}
            onRequestClose={() => onClickCloseTool(tool)}
            dwbuid={accountData.currentDwbuid}
            toolId={tool.id}
          />
        );
    }
  };

  return (
    <main className={styles.main}>
      {numGetListenFailures > MAX_NUM_GET_LISTEN_FAILURES &&
      !hideGetListenFailureBanner ? (
        <WarningBanner
          message={strings.getListenFailure}
          onDismiss={() => setHideGetListenFailureBanner(true)}
        />
      ) : null}
      {isSuperseded ? <WarningBanner message={strings.superseded} /> : null}
      <div className={styles.mainContent}>
        {isLoading || isFetching ? (
          <Container>
            <Row>
              <Col offset={{ xs: 6 }} xs={1}>
                <Spinner className={styles.spinner} />
              </Col>
            </Row>
          </Container>
        ) : isError ? (
          <Container>
            <Row>
              <Col>
                <StyledError
                  error={error}
                  errorTitle={strings.errorTitle}
                  refetch={refetch}
                  footer={
                    <div className={styles.errorFooter}>
                      {strings.returnToAccountSearchP1}
                      <StyledLink
                        to={paths.accountSearch}
                        state={
                          callContext
                            ? { callContext: { ...callContext, dwbuid: null } }
                            : null
                        }
                      >
                        {strings.accountSearchPage}
                      </StyledLink>
                      {strings.returnToAccountSearchP2}
                    </div>
                  }
                />
              </Col>
            </Row>
          </Container>
        ) : (
          <div
            className={classNames(
              styles.loadedContent,
              isGetCallInfoLoading && styles.callInfoLoadingDisabled,
            )}
          >
            {isGetCallInfoLoading ? (
              <div className={styles.callInfoLoadingOverlay} />
            ) : null}
            <Toolbar
              account={getAccountData}
              toolState={toolState}
              scrollToCompleted={() => {
                document
                  .getElementById(COMPLETED_TOOLS_ID)
                  ?.scrollIntoView({ behavior: "smooth" });
              }}
              isDebtDisclosureLoaded={
                !!accountDetailsQueryResult.data?.debtDisclosure
              }
              isDebtDisclosureComplete={isDebtDisclosureComplete}
              isResultComplete={isResultComplete}
              isResultRequired={isResultRequired}
            />
            <div
              className={classNames(
                styles.pageContentContainer,
                styles.pageContent,
              )}
            >
              <div className={styles.pageContent}>
                <AccountDetailsHeader
                  account={getAccountData}
                  accountDetailsQueryResult={accountDetailsQueryResult}
                  onDisplayGeneralErrors={onDisplayGeneralErrors}
                />
                {accountDetailsQueryResult.data && (
                  <DataPanel accountDetails={accountDetailsQueryResult.data} />
                )}
                <TransferMessages
                  accountDetailsQueryResult={accountDetailsQueryResult}
                  debtDisclosureWarning={
                    accountDetailsQueryResult.data?.debtDisclosure?.warning
                  }
                />
                {toolState.activeTools.map((tool) =>
                  renderTool(tool, getAccountData),
                )}
                <div id={COMPLETED_TOOLS_ID} />
                {toolState.completedTools.map((tool) => (
                  <CompletedTool
                    key={tool.id}
                    className={styles.summary}
                    summary={tool.completed?.summary ?? ""}
                    type={tool.type}
                  />
                ))}
              </div>
              <NotesFooter
                note={note}
                setNote={setNote}
                isCritical={isNoteCritical}
                setIsCritical={setIsNoteCritical}
                validationMessages={noteValidationMessages}
                setValidationMessages={setNoteValidationMessages}
                onSubmit={onSubmitNote}
                isSubmitting={isAddNoteLoading}
                channel={channel}
              />
            </div>
            <NetworkModal
              status={displayError?.status}
              message={displayError?.message}
              onRequestClose={() => setDisplayError(undefined)}
            />
            {/* First priority: If isBlockerModalOpen is true and Debt Disclosure Required should be shown */}
            <DebtDisclosureRequiredModal
              isOpen={
                isBlockerModalOpen &&
                isDebtDisclosureBlocking({
                  debtDisclosure:
                    accountDetailsQueryResult.data?.debtDisclosure,
                  isDebtDisclosureComplete,
                  activeTools: toolState.activeTools,
                })
              }
              onRequestClose={() => {
                handleShouldBlockProceed(false);
                if (accountDetailsQueryResult.data?.debtDisclosure) {
                  toolState.onAddTool({
                    type: ToolType.DebtDisclosure,
                    inProgress: false,
                  });
                }
              }}
            />
            {/* Second priority: If isBlockerModalOpen is true and Result Required should be shown */}
            <WarningModal
              isOpen={
                isBlockerModalOpen &&
                !isDebtDisclosureBlocking({
                  debtDisclosure:
                    accountDetailsQueryResult.data?.debtDisclosure,
                  isDebtDisclosureComplete,
                  activeTools: toolState.activeTools,
                }) &&
                isResultBlocking({
                  isResultComplete,
                })
              }
              isConfirmLoading={isResultResearchOnlyLoading}
              confirmText={
                !isResultRequired ? strings.endSessionText : undefined
              }
              onConfirm={async () => {
                if (isResultRequired) {
                  return null;
                }
                try {
                  await submitResultResearchOnly({
                    dwbuid,
                    referenceId: getCallInfoData?.referenceId,
                  });
                } catch (err) {
                  reportError(
                    new Error("Programmer Error: Error ending research call"),
                    {
                      error: err,
                    },
                  );
                } finally {
                  handleShouldBlockProceed(true);
                }
              }}
              modalLabel={strings.resultModalLabel(channel)}
              body={
                isResultRequired
                  ? strings.resultMustComplete(channel)
                  : strings.resultCanComplete(channel)
              }
              closeText={strings.resultButtonText}
              onRequestClose={() => {
                handleShouldBlockProceed(false);
                toolState.onAddTool({
                  type: ToolType.Results,
                  inProgress: false,
                });
              }}
            />
            {/* Third priority: If isBlockerModalOpen is true and the user has in progress tools */}
            <CloseToolConfirmationModal
              isOpen={
                isBlockerModalOpen &&
                !isDebtDisclosureBlocking({
                  debtDisclosure:
                    accountDetailsQueryResult.data?.debtDisclosure,
                  isDebtDisclosureComplete,
                  activeTools: toolState.activeTools,
                }) &&
                !isResultBlocking({
                  isResultComplete,
                })
              }
              toolNames={inProgressToolNames}
              onCloseTool={() => handleShouldBlockProceed(true)}
              onRequestClose={() => handleShouldBlockProceed(false)}
            />
            <CloseToolConfirmationModal
              isOpen={!!closeConfirmationTool}
              toolNames={
                closeConfirmationTool
                  ? [toolTypeToName(closeConfirmationTool.type)]
                  : []
              }
              onCloseTool={() => {
                if (closeConfirmationTool) {
                  onClickCloseTool(closeConfirmationTool);
                }
              }}
              onRequestClose={() => setCloseConfirmationTool(undefined)}
            />
            <WarningModal
              modalLabel={strings.getCallInfoErrorModal}
              isOpen={isGetCallInfoError}
              body={<div>{strings.getCallInfoError}</div>}
              closeText={strings.retry}
              onRequestClose={getCallInfoRefetch}
            />
          </div>
        )}
      </div>
    </main>
  );
};
export default AccountDetailsPage;
